import "./demoSetup"; // must be first — patches fetch/XHR in demo mode
import React, { Suspense, lazy, useEffect } from "react";
import ReactDOM from "react-dom/client";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import "./index.css";
import useAuth, { AuthProvider } from "./hooks/useAuth";
import useTheme, { ThemeProvider } from "./hooks/useTheme";
import reportWebVitals from "./reportWebVitals";
import RouteError, { clearChunkReload } from "./routes/routeError";

// Split per route so each page only downloads its own dependencies (e.g.
// react-dropzone is only needed on Home).
const loadHome = () => import("./routes/home");
const loadGallery = () => import("./routes/gallery");
const loadApiKeys = () => import("./routes/apikeys");
const loadLogin = () => import("./routes/login");
const loadNotFound = () => import("./routes/notFound");
const Home = lazy(() => loadHome().then(clearChunkReload));
const Gallery = lazy(() => loadGallery().then(clearChunkReload));
const ApiKeys = lazy(() => loadApiKeys().then(clearChunkReload));
const Login = lazy(() => loadLogin().then(clearChunkReload));
const NotFound = lazy(() => loadNotFound().then(clearChunkReload));

// The auth guards don't render the page until the session check resolves, so
// lazy() alone would wait for it before fetching the chunk. Start the chunk
// the guards will land on now; webpack reuses the in-flight request when
// lazy() asks. An expired stored token still lands on Login after the check.
function prefetchLandingRoute() {
  const protectedLoaders = {
    "/": loadHome,
    "/gallery": loadGallery,
    "/api-keys": loadApiKeys,
  };
  // Normalize the way the router matches: case-insensitive, trailing
  // slashes ignored.
  const path =
    window.location.pathname.toLowerCase().replace(/\/+$/, "") || "/";
  if (!(path in protectedLoaders) && path !== "/login") return;

  let hasSession = false;
  try {
    hasSession = JSON.parse(localStorage.getItem("sessionToken")) !== null;
  } catch {}

  let load;
  if (!hasSession) load = loadLogin;
  else if (path === "/login") load = loadHome;
  else load = protectedLoaders[path];

  // A failure here is retried when lazy() imports the route for real.
  load().catch(() => {});
}
prefetchLandingRoute();

function AuthLoading() {
  return (
    <div className="auth-loading">
      <div className="auth-spinner" />
    </div>
  );
}

// Navigate renders nothing, and the transition it starts keeps that empty
// screen up while the target route's chunk loads, so show the spinner with it.
function Redirect({ to }) {
  return (
    <>
      <Navigate to={to} replace />
      <AuthLoading />
    </>
  );
}

function RequireAuth({ children }) {
  const { sessionToken, isValidating } = useAuth();

  // Don't render the protected page until we know the stored token is valid —
  // otherwise an expired token flashes the logged-in UI before bouncing here.
  if (isValidating) return <AuthLoading />;

  return sessionToken === null ? <Redirect to="/login" /> : children;
}

function RequireNoAuth({ children }) {
  const { sessionToken, isValidating } = useAuth();

  // Mirror RequireAuth: wait for the stored token to be validated before
  // deciding, otherwise /login bounces through / on an expired cached token.
  if (isValidating) return <AuthLoading />;

  return sessionToken !== null ? <Redirect to="/" /> : children;
}

function ThemeApplier({ children }) {
  const { isDark } = useTheme();

  useEffect(() => {
    const theme = isDark ? "dark" : "light";
    document.documentElement.setAttribute("data-theme", theme);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", isDark ? "#121212" : "#ffffff");
    }
  }, [isDark]);

  return children;
}

const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      {
        path: "/",
        element: (
          <RequireAuth>
            <Home />
          </RequireAuth>
        ),
      },
      {
        path: "/gallery",
        element: (
          <RequireAuth>
            <Gallery />
          </RequireAuth>
        ),
      },
      {
        path: "/api-keys",
        element: (
          <RequireAuth>
            <ApiKeys />
          </RequireAuth>
        ),
      },
      {
        path: "/login",
        element: (
          <RequireNoAuth>
            <Login />
          </RequireNoAuth>
        ),
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
]);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ThemeProvider>
      <AuthProvider>
        <ThemeApplier>
          <Suspense fallback={<AuthLoading />}>
            {/* Navigating in a transition keeps the current page up while a
                lazy route's chunk loads, instead of the root fallback. */}
            <RouterProvider
              router={router}
              future={{ v7_startTransition: true }}
            />
          </Suspense>
        </ThemeApplier>
      </AuthProvider>
    </ThemeProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// The route chunks are small, so fetch the rest once the page has loaded;
// later navigations, including auth redirects, then don't wait on the network.
window.addEventListener("load", () => {
  const prefetchAll = () =>
    [loadHome, loadGallery, loadApiKeys, loadLogin, loadNotFound].forEach(
      (load) => load().catch(() => {}),
    );
  if ("requestIdleCallback" in window) window.requestIdleCallback(prefetchAll);
  else setTimeout(prefetchAll, 1000);
});
